import DairyService from '@src/api/services/dairy';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { StateEl } from '../style';
import { FormFields } from '@src/components/FormFields';
import { GlobalEl } from '@src/styles/app';
import DairyQuery from '@src/QueryHooks/DairyQuery';
import notify from '~/components/Notify';
import { useMutation } from 'react-query';
import Drawer from '~/components/Drawer';
import { useAuth } from '~/routes/components/AuthProvider';
import { StyledTabPane } from '~/pages/Dairy/Settings/General/style';
import { Tab } from 'semantic-ui-react';
import { StyledMenuItem } from '~/pages/Dairy/CloudStorage/style';

interface CreateEditMilkTypeProps {
  isVisible: boolean;
  handleHiddenDrawer: () => void;
  isLoading?: boolean;
  fetchData?: () => void,
  idMilkType?: any
}

const CreateEditMilkTypes: React.FunctionComponent<CreateEditMilkTypeProps> = (props) => {
  const { isVisible, handleHiddenDrawer, idMilkType } = props;
  const { t } = useTranslation();
  const { systemData: { languages } } = useAuth()
  const { handleSubmit, control, reset, watch, formState } = useForm({
    mode: 'onSubmit'
  });

  const watchDefaultField = watch('default')

  useEffect(() => {
    if (idMilkType) {
      editMilkType(idMilkType);
    } else {
      const languages = getLangs()
      reset({
        status: 0,
        default: 0,
        override: 0,
        languages
      });
    }
  }, [isVisible, idMilkType]);

  function getLangs() {
    return languages.map(language => ({
      id: language.id,
      abbreviation: '',
      name: ''
    }))
  }

  const onSuccess = () => {
    notify(t('Successfully created milk type'), 'success');
    props.fetchData()
  };

  function getParsedLanguages(milkTypeLanguages) {
    languages.map((language) => {
      const index = milkTypeLanguages.findIndex(languageText => languageText.id === language.id)
      if (index < 0) {
        milkTypeLanguages.push({
          id: language.id,
          abbreviation: '',
          name: '',
          order: language.order
        })
      } else {
        milkTypeLanguages[index].order = language.order
      }
    })
    milkTypeLanguages.sort((a, b) => a.order - b.order)
    milkTypeLanguages?.forEach(lang => delete lang.order)
    return milkTypeLanguages
  }

  const onSuccessGetMilkType = (response) => {
    response.data.languages = getParsedLanguages(response.data.languages)
    reset(response.data)
  }

  const { mutateAsync: createMilkType } =
    DairyQuery.createMilkType(onSuccess);

  const { mutate: updateMilkType } = useMutation(
    'milk-type-update',
    DairyService.updateMilkType,
    {
      onSuccess: () => {
        notify(t('Successfully updated milk type'), 'success');
        props.fetchData()
      }
    }
  );

  const { isLoading: isMilkTypeLoading, mutateAsync: editMilkType } =
    DairyQuery.editMilkType(onSuccessGetMilkType);

  const onSubmit = (data) => {
    if (!idMilkType) {
      createMilkType({ ...data });
    } else {
      updateMilkType({ id: idMilkType, data })
    }
    handleHiddenDrawer();
  };

  function errorTab() {
    //@ts-ignore
    return formState.errors?.languages?.map((lang, index) => index)?.filter(item => typeof (item) === 'number')
  }

  function getMenuItem(label, index) {
    const errors = errorTab() || []

    return <StyledMenuItem
      key={label}
      className='tab-item'
    >
      <div className={`${errors.includes(index) ? 'error' : ''}`}>{label}</div>
    </StyledMenuItem>
  }

  function renderLanguageFields() {
    return <Tab
      menu={{ secondary: true, pointing: true }}
      renderActiveOnly={false}
      panes={
        languages.map((language) => {
          const indexLanguage = languages.findIndex(languageIndex => languageIndex['id'] === language.id)
          return {
            //@ts-ignore
            menuItem: getMenuItem(language['label'], indexLanguage),
            pane: <StyledTabPane key={language.id}>
              <FormFields.Input
                control={control}
                name={`languages[${indexLanguage}].name`}
                label={t('Designation')}
                placeholder={t('Designation')}
                rules={{
                  required: {
                    value: true,
                    message: t('Please enter designation')
                  }
                }}
              />
              <FormFields.Input
                control={control}
                label={t('Abbreviation')}
                placeholder={t('Abbreviation')}
                name={`languages[${indexLanguage}].abbreviation`}
                rules={{
                  required: {
                    value: true,
                    message: t('Please enter abbreviation')
                  },
                  maxLength: {
                    value: 20,
                    message: t('Max. 20 characters')
                  }
                }}
              />
            </StyledTabPane>
          }
        })}
    />
  }

  const renderContent = () => {
    return (
      <>
        <FormFields.Checkbox
          control={control}
          name={'status'}
          defaultValue={0}
          checkboxValue={1}
          label={t('Status')}
        />
        <FormFields.Checkbox
          control={control}
          name={'default'}
          checkboxValue={1}
          defaultValue={0}
          label={t('Default')}
        />
        {!idMilkType && watchDefaultField === 1 && <FormFields.Checkbox
          control={control}
          checkboxValue={1}
          name={'override'}
          defaultValue={0}
          label={t('Override')}
        />
        }
        {renderLanguageFields()}
        <StateEl.DrawerFooter>
          <GlobalEl.PrimaryButton
            disabled={Object.keys(formState.dirtyFields).length === 0}
          >{idMilkType ? t('Update') : t('Add')}</GlobalEl.PrimaryButton>
        </StateEl.DrawerFooter>
      </>
    );
  };

  return <Drawer
    isVisible={isVisible}
    as
    handleSubmit={handleSubmit(onSubmit)}
    isBusy={isMilkTypeLoading}
    header={idMilkType ? t('Edit milk type') : t('Create milk type')}
    closeOnEscape
    onHide={() => {
      //   setUpdateLogsData(null);
      reset();
      handleHiddenDrawer();
    }}
    content={renderContent()}
  />
};

export default CreateEditMilkTypes;
